import { useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useTenants } from '../../lib/api/hooks.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Activity across every customer and the platform itself.
 *
 * The tenant console has an audit tab that answers "what happened to my
 * account". This answers an operator's questions instead: what just changed,
 * at which customer, by whom, and did anything fail. The account column is the
 * headline difference — without it `extension.deleted` is unreadable, because
 * you cannot tell whether it happened at your largest customer or one you have
 * never heard of.
 */

interface Row {
  id: string; at: string; action: string;
  resource_type: string; resource_id: string | null;
  tenant_id: string | null; tenant_name: string | null;
  actor_type: string; actor: string; actor_email: string | null;
  impersonated_by: string | null; impersonated_by_email: string | null;
  reason: string | null; result: string;
  ip: string | null; request_id: string | null; has_diff: boolean;
}
interface Page { data: Row[]; pagination: { next_cursor: string | null; has_more: boolean } }

/**
 * Plain language for the actions worth naming, and a rule for the rest.
 *
 * A column of dotted identifiers is scanned far more slowly than a column of
 * sentences, and `extension.credential_revealed` only reads as the serious
 * thing it is once it says so. But the list grows every sprint — 89 action
 * names today, from 87 audited routes — so a fixed map without a fallback
 * renders blanks for whatever shipped last week. The fallback below is built
 * from the action itself and is never empty.
 *
 * The raw string stays visible in the row's title and in the expanded detail:
 * it is what an operator greps, quotes in a ticket, and filters by.
 */
const PHRASE: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed sign-in',
  'auth.step_up': 'Re-verified identity',
  'auth.step_up_failed': 'Failed re-verification',
  'auth.sessions_revoked': 'Signed out other sessions',
  'billing.balance_adjusted': 'Adjusted balance by hand',
  'billing.account_created': 'Set up billing',
  'billing.account_updated': 'Changed billing terms',
  'ratedeck.published': 'Published a rate deck',
  'ratedeck.rules_imported': 'Imported tariff rules',
  'extension.credential_revealed': 'Revealed a SIP password',
  'extension.credential_rotated': 'Rotated a SIP password',
  'device.provisioning_opened': 'Reopened handset provisioning',
  'invitation.created': 'Invited someone',
  'invitation.accepted': 'Accepted an invitation',
  'invitation.revoked': 'Revoked an invitation',
  'membership.removed': 'Removed someone’s access',
  'apikey.created': 'Created an API key',
  'trunk.deleted': 'Deleted a trunk',
  'tenant_trunk.linked': 'Linked a carrier',
  'tenant_trunk.unlinked': 'Unlinked a carrier',
};

const VERB: Record<string, string> = {
  created: 'Created', updated: 'Changed', deleted: 'Deleted', archived: 'Archived',
  linked: 'Linked', unlinked: 'Unlinked', published: 'Published', applied: 'Applied',
  removed: 'Removed', revoked: 'Revoked', accepted: 'Accepted', assigned: 'Assigned',
  released: 'Released', added: 'Added', configured: 'Configured', uploaded: 'Uploaded',
  set: 'Set', rotated: 'Rotated', opened: 'Opened',
};

function phraseFor(r: Row): string {
  const known = PHRASE[r.action];
  if (known) return known;
  const verb = VERB[r.action.split('.').pop() ?? ''] ?? 'Changed';
  const noun = r.resource_type.replace(/_/g, ' ');
  // "Changed a extension" was the first thing anyone noticed on the page. The
  // fallback is generated, so the article has to be chosen rather than assumed.
  const article = /^[aeiou]/i.test(noun) ? 'an' : 'a';
  return `${verb} ${article} ${noun}`;
}

const WINDOWS = [
  { v: 1, label: 'Last 24 hours' },
  { v: 7, label: 'Last 7 days' },
  { v: 30, label: 'Last 30 days' },
  { v: 90, label: 'Last 90 days' },
];

const CATEGORIES = [
  ['', 'All activity'], ['auth', 'Sign-ins'], ['billing', 'Billing & rates'],
  ['carriers', 'Carriers & trunks'], ['numbers', 'Numbers & routing'],
  ['extensions', 'Extensions & devices'], ['callflow', 'Call handling'],
  ['access', 'Users & access'], ['accounts', 'Accounts'],
] as const;

export function ActivityPage() {
  const [days, setDays] = useState(1);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const tenants = useTenants({ limit: 200 });

  const q = useInfiniteQuery({
    queryKey: ['activity', days, category, search, tenantId, failedOnly],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const sp = new URLSearchParams({ days: String(days), limit: '50' });
      if (category) sp.set('category', category);
      if (search) sp.set('search', search);
      if (tenantId === 'platform') sp.set('scope', 'platform');
      else if (tenantId) sp.set('tenant_id', tenantId);
      if (failedOnly) sp.set('failed_only', 'true');
      if (pageParam) sp.set('cursor', pageParam);
      return api.get<Page>(`/v1/activity?${sp}`);
    },
    getNextPageParam: (last) => last.pagination.next_cursor,
  });

  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Activity log</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Every change across every customer and the platform, newest first.
          </p>
        </div>
      </div>

      {q.error && <ErrorBanner error={q.error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search who, or an action…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              aria-label="Search activity" />
          </div>
          <select className="arc-select" style={{ width: 'auto' }} value={tenantId}
            aria-label="Filter by account"
            onChange={(e) => setTenantId(e.target.value)}>
            <option value="">All accounts</option>
            <option value="platform">Platform only</option>
            {tenants.data?.data.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select className="arc-select" style={{ width: 'auto' }} value={category}
            aria-label="Filter by category"
            onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <select className="arc-select" style={{ width: 'auto' }} value={days}
            aria-label="Time range"
            onChange={(e) => setDays(Number(e.target.value))}>
            {WINDOWS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
          </select>
          {/*
            Named "Failed sign-ins", not "Failures", because that is all it can
            ever return. audit() writes inside the caller's transaction, so a
            mutation that fails rolls its own audit row back — by design, since
            a log of changes that did not happen is worse than one with gaps.
            The only failure rows that survive are the two auth ones, which are
            written outside a transaction precisely so they do.
          */}
          <button type="button"
            className={`arc-btn arc-btn-sm ${failedOnly ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
            onClick={() => setFailedOnly((v) => !v)}>
            Failed sign-ins
          </button>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {q.data ? `${rows.length}${q.hasNextPage ? '+' : ''} shown` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table arc-table-fixed">
            {/* Widths measured against the content, not chosen: an actor's full
                name runs to ~170px and an account name to ~180px, so those two
                get the room and the rest are pinned to what they need. */}
            <colgroup>
              <col style={{ width: 108 }} />
              <col style={{ width: 224 }} />
              <col style={{ width: 190 }} />
              <col />
              <col style={{ width: 128 }} />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead>
              <tr>
                <th>When</th><th>Who</th><th>Account</th>
                <th>Did what</th><th>From</th><th />
              </tr>
            </thead>
            {q.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((r) => {
                  const failed = r.result !== 'success';
                  return (
                    <RowPair key={r.id} r={r} failed={failed}
                      expanded={expanded === r.id}
                      onToggle={() => setExpanded(expanded === r.id ? null : r.id)} />
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {!q.isLoading && rows.length === 0 && (
          <Empty icon="activity" title="Nothing in this window"
            hint="Widen the time range, or clear the filters. Sign-ins, billing changes and configuration edits all appear here." />
        )}

        {q.hasNextPage && (
          <div className="arc-card-body" style={{ textAlign: 'center' }}>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}>
              {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function RowPair({ r, failed, expanded, onToggle }: {
  r: Row; failed: boolean; expanded: boolean; onToggle: () => void;
}) {
  return (
    <>
      <tr className="arc-clickable" onClick={onToggle}>
        <td className="arc-muted arc-truncate" title={new Date(r.at).toLocaleString('en-ZA')}>
          {relativeTime(r.at)}
        </td>
        <td>
          <span className="arc-truncate" style={{ fontWeight: 550 }}
            title={r.actor_email ?? r.actor}>
            {r.actor}
          </span>
          {/*
            An API key acting is a materially different answer to "who did this"
            than a person acting, and the actor column alone cannot say so — a
            service principal has no user row to resolve a name from, so it
            would otherwise render as the bare word "service".
          */}
          {r.actor_type !== 'user' && (
            <span className="arc-subtle" style={{ fontSize: 11, marginLeft: 6 }}>
              {r.actor_type}
            </span>
          )}
          {/* Impersonation, when it exists: "we changed it as the customer" and
              "the customer changed it" are different answers to one question. */}
          {r.impersonated_by && (
            <div className="arc-subtle" style={{ fontSize: 11, marginTop: 2 }}>
              as {r.impersonated_by_email ?? r.impersonated_by}
            </div>
          )}
        </td>
        <td className="arc-muted">
          {r.tenant_id
            ? (
              <Link to="/accounts/$accountId" params={{ accountId: r.tenant_id }}
                className="arc-truncate" style={{ color: 'var(--arc-accent)' }}
                onClick={(e) => e.stopPropagation()}>
                {r.tenant_name ?? r.tenant_id.slice(0, 8)}
              </Link>
            )
            /* "Platform" is a first-class value here, not a blank. A fifth of
               all rows belong to no customer — every sign-in, every carrier and
               rate-deck change — and a blank cell would read as missing data. */
            : <span className="arc-subtle">Platform</span>}
        </td>
        <td title={r.action}>
          {/*
            COLOUR, not a pill.
            A failure is worth marking and does not need a container to do it --
            the word "Failed sign-in" in red says everything the badge said. The
            pill also carried padding, which made failure rows 38px against the
            35px of every other row, so the one thing on the page that should
            look deliberate was also the one thing making it ragged.
          */}
          <span style={failed
            ? { color: 'var(--arc-danger)', fontWeight: 550 }
            : undefined}>
            {phraseFor(r)}
          </span>
          {r.resource_id && (
            <span className="arc-mono arc-subtle" style={{ marginLeft: 7, fontSize: 11 }}>
              {r.resource_id.slice(0, 8)}
            </span>
          )}
        </td>
        <td className="arc-mono arc-muted arc-truncate" style={{ fontSize: 11.5 }}>
          {r.ip ?? <span className="arc-subtle">—</span>}
        </td>
        <td className="arc-td-right">
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={13} />
        </td>
      </tr>
      {expanded && <DetailRow r={r} />}
    </>
  );
}

/**
 * The before/after, fetched only when a row is opened.
 *
 * The list query deliberately does not select these: they are jsonb and
 * therefore stored out of line, so including them detoasts one document per row
 * to render the one somebody eventually expands.
 */
function DetailRow({ r }: { r: Row }) {
  const d = useQuery({
    queryKey: ['activity-detail', r.id],
    queryFn: () => api.get<{
      before: Record<string, unknown> | null;
      after: Record<string, unknown> | null;
      reason: string | null; user_agent: string | null; request_id: string | null;
    }>(`/v1/activity/${r.id}`),
  });

  return (
    <tr>
      <td colSpan={6} style={{ background: 'var(--arc-surface-2)' }}>
        {d.isLoading ? (
          <span className="arc-subtle" style={{ fontSize: 12 }}>Loading…</span>
        ) : d.error ? (
          <ErrorBanner error={d.error} />
        ) : (
          <>
            <div className="arc-subtle arc-mono" style={{ fontSize: 11, marginBottom: 8 }}>
              {r.action}
              {r.resource_id && <> · {r.resource_id}</>}
            </div>
            {d.data?.reason && (
              <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
                {d.data.reason}
              </p>
            )}
            {(d.data?.before || d.data?.after) ? (
              <div className="arc-grid arc-grid-2">
                <div>
                  <div className="arc-label">Before</div>
                  <pre className="arc-diff">{fmt(d.data.before)}</pre>
                </div>
                <div>
                  <div className="arc-label">After</div>
                  <pre className="arc-diff">{fmt(d.data.after)}</pre>
                </div>
              </div>
            ) : (
              <p className="arc-subtle" style={{ fontSize: 12 }}>
                Nothing else was recorded for this entry.
              </p>
            )}
            {d.data?.request_id && (
              <p className="arc-subtle" style={{ fontSize: 11, marginBottom: 0 }}>
                request {d.data.request_id}
              </p>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

function fmt(v: Record<string, unknown> | null | undefined): string {
  if (!v || Object.keys(v).length === 0) return '—';
  return Object.entries(v)
    .map(([k, val]) => `${k}: ${typeof val === 'object' ? JSON.stringify(val) : String(val)}`)
    .join('\n');
}
